import { TEMPLATES } from '@npc/copy'
import type { Schemas } from '@npc/api-client'

import type { CopyId } from '../../copy'
import { Tag, type TagTone } from '../../components/tag'
import { T } from '../../copy/text'

/**
 * 每条时间线右上角那个标签。
 *
 * 两件事都**不在前端判**：
 *
 *   说什么   `timeline.tag.<枚举>` 那六条模板，id 由后端给的 `tag` 字段拼出来
 *   什么颜色 同一条模板自己的 `tone`
 *
 * 也就是说这个组件里没有一处「什么情况算警告」的判断。这不是洁癖：
 * 「例行」与「动手了」可以都是绿级、都成功，说法和颜色却不一样——
 * 从 `risk` 或 `status` 推是推不出来的，契约里 `ActionTag` 是独立字段正是这个原因。
 *
 * `tag` 现在可空（流 A3 的 Go 结构体还没有这个字段），空就整个标签不显示。
 */
const TAG_IDS = {
  noted: 'timeline.tag.noted',
  acted: 'timeline.tag.acted',
  routine: 'timeline.tag.routine',
  code_changed: 'timeline.tag.code_changed',
  deployed: 'timeline.tag.deployed',
  trouble: 'timeline.tag.trouble',
  // `satisfies Record<..., CopyId>` 是这张表的全部意义所在：
  //   枚举漏一档     编译期红（Record 的键必须铺满 ActionTag）
  //   id 拼错一个字  编译期红（值必须在 CopyId 里）
  // 拼出来的模板 id 没人对，正是流 0 在 M0-35 里点名的那类洞。这里不留那个洞。
} as const satisfies Record<Schemas['ActionTag'], CopyId>

const TONES = new Set<string>(['neutral', 'ok', 'warn', 'danger', 'info'])

export function ActionTag({ tag }: { tag: Schemas['ActionTag'] }) {
  const id = TAG_IDS[tag]
  return (
    <Tag tone={toneOf(id)}>
      <T id={id} />
    </Tag>
  )
}

/**
 * 模板自己的 `tone` 拿来当颜色。
 *
 * 模板里的 `tone` 类型是 `string`（可以为空），`Tag` 要的是五档之一，
 * 所以这里过一道。**认不出来就退回 neutral，不猜**——
 * 猜错的颜色比没有颜色更糟：一条例行备份被涂成红的，用户会以为出事了。
 */
function toneOf(id: CopyId): TagTone {
  const tone = TEMPLATES[id].tone
  return TONES.has(tone) ? (tone as TagTone) : 'neutral'
}
